import { useEffect, useState, useRef} from 'react';
import { edgeRefusalMessage } from '@/utils/assignment/smartAssignRefusal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { ArrowRight, CheckCircle, XCircle, Sparkles, Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useAuth } from '@/contexts/AuthContext';
import { notifyDayboardInvalidated } from '@/utils/dayboardInvalidation';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { cn } from '@/lib/utils';
import {
  deriveSuggestionsSplitOperationId,
  effectiveScheduledStartTime,
} from '@/lib/tickets/splitSurfaceIdentity';
// DTC-J: route the optimizer-suggestions shop-zoned "now" anchor
// through the canonical clock so the suggestions panel is deterministic
// under `clock.setFixed(...)`. Same drift class as DTC-F V11
// StaffPresenceIndicator.
import { clock } from '@/utils/clock';

interface OptimizerAction {
  id: string;
  ticket_id: string;
  from_json: { techId: string | null; startTime: string | null };
  to_json: { techId: string; startTime: string };
  reason: string;
  delta_score: number;
  metadata?: {
    type?: 'split' | 'cross-tech' | 'compact';
    splitType?: 'proficiency' | 'skill-level';
    proficiencyGap?: number;
    skillGap?: number;
    from_tech_name?: string | null;
    to_tech_name?: string | null;
    ticket?: {
      customer_name?: string;
      customer_phone?: string;
      vehicle_year?: number;
      vehicle_make?: string;
      vehicle_model?: string;
      vehicle_color?: string;
      type?: string;
      due_out?: string;
      predicted_minutes?: number;
      jobs?: Array<{
        id: string;
        name: string;
        category: string;
        skill_level: number;
        standard_minutes: number;
      }>;
    };
    splitGroups?: Array<{
      jobIds: string[];
      jobs: Array<{ id: string; name: string }>;
      avgSkillLevel: number;
      categories: string[];
      totalMinutes: number;
      suggestedTechId?: string;
      suggestedTechName?: string;
      reasoning: string;
    }>;
    isWaiterOrScheduled?: boolean;
  };
}

interface SuggestionsDrawerProps {
  dateISO: string;
}

export function SuggestionsDrawer({ dateISO }: SuggestionsDrawerProps) {
  const [suggestions, setSuggestions] = useState<OptimizerAction[]>([]);
  const [loading, setLoading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const { activeShop } = useAuth();
  const { shopTimezone } = useShopTimezone();

  useEffect(() => {
    loadSuggestions();

    const handleOptimizerComplete = () => {
      loadSuggestions();
    };

    window.addEventListener('optimizer-completed', handleOptimizerComplete);
    return () => window.removeEventListener('optimizer-completed', handleOptimizerComplete);
  }, [dateISO]);

  const loadSuggestions = async () => {
    try {
      if (!activeShop?.id) return;

      const { data: profile, error: profileError } = await supabase
        .from('shop_profile')
        .select('id')
        .eq('shop_id', activeShop.id)
        .maybeSingle();

      if (profileError) throw profileError;
      if (!profile?.id) return;

      const { data: runs, error: runsError } = await supabase
        .from('optimizer_runs')
        .select('id')
        .eq('date_iso', dateISO)
        .eq('shop_id', profile.id)
        .order('created_at', { ascending: false })
        .limit(1);

      if (runsError) throw runsError;
      if (!runs || runs.length === 0) return;

      const { data: actions, error: actionsError } = await supabase
        .from('optimizer_actions')
        .select('*')
        .eq('run_id', runs[0].id)
        .eq('applied', false)
        .order('delta_score', { ascending: true });

      if (actionsError) throw actionsError;
      setSuggestions((actions || []) as unknown as OptimizerAction[]);
    } catch (error) {
      console.error('Error loading suggestions:', error);
    }
  };

  const triggerReOptimization = async () => {
    if (!activeShop?.id) return;
    
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      const { data, error } = await supabase.functions.invoke('optimize-board', {
        body: { 
          shopId: activeShop.id, 
          dateISO, 
          mode: 'cascade',
          invalidatePrevious: true
        },
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
      });

      if (error) throw error;
      
      window.dispatchEvent(new CustomEvent('optimizer-completed'));
    } catch (error) {
      console.error('Re-optimization failed:', error);
      toast({ 
        title: 'Re-optimization Failed', 
        description: 'Could not update remaining suggestions',
        variant: 'destructive' 
      });
    }
  };

  const applySuggestion = async (action: OptimizerAction) => {
    const hasOtherSuggestions = suggestions.length > 1;
    
    setLoading(true);
    try {
      if (action.metadata?.type === 'split' && action.to_json.techId === 'SPLIT-REQUIRED') {
        toast({ title: 'Split Execution Required', description: 'Please use the "Execute Split" button', variant: 'destructive' });
        setLoading(false);
        return;
      }

      // MED 3.3 Guard 1: Validate startTime is present and parseable
      if (!action.to_json?.startTime || isNaN(new Date(action.to_json.startTime).getTime())) {
        toast({ title: 'Invalid Suggestion', description: 'Start time is missing or invalid. Run optimizer again.', variant: 'destructive' });
        setLoading(false);
        return;
      }

      // MED 3.3 Guard 2: Block if suggested time is now in the past (shop-TZ aware)
      const shopNow = toZonedTime(clock.now(), shopTimezone);
      const suggestedTime = toZonedTime(new Date(action.to_json.startTime), shopTimezone);
      if (suggestedTime < shopNow) {
        toast({ title: 'Suggestion Expired', description: 'The scheduled time is now in the past. Run optimizer again.', variant: 'destructive' });
        setLoading(false);
        return;
      }

      // MED 3.3 Guard 3: Block if ticket is in parking lot (prevents zombie assignments)
      const { data: ticketCheck } = await supabase
        .from('tickets')
        .select('parking_lot, scheduled_date')
        .eq('id', action.ticket_id)
        .maybeSingle();
      if (ticketCheck?.parking_lot) {
        toast({ title: 'Ticket in Parking Lot', description: 'Restore this ticket from the Parking Lot before applying suggestions.', variant: 'destructive' });
        setLoading(false);
        return;
      }

      // FIX-8 R47: Guard against null scheduled_date
      if (!ticketCheck?.scheduled_date) {
        toast({ title: 'Cannot apply suggestion', description: 'Ticket has no scheduled date.', variant: 'destructive' });
        setLoading(false);
        return;
      }

      // Route through reassign-ticket to ensure chain following, wall-time propagation,
      // bay validation, and cascade triggering all happen atomically.
      const { error: reassignError } = await supabase.functions.invoke('reassign-ticket', {
        body: {
          ticketId: action.ticket_id,
          targetTechId: action.to_json.techId,
          scheduledDate: ticketCheck?.scheduled_date,
          desiredStartTime: action.to_json.startTime,
          moveReason: 'optimizer-suggestion',
          // Core-100 R4 W3 (GAP-013): the optimizer's own (server) record of this suggestion — reassign-ticket checks
          // the target against it, so the origin is server-known.
          optimizerActionId: action.id,
        },
      });

      if (reassignError) throw reassignError;

      // FIX-R35-CRIT-3: surface optimizer_actions update failure. Previously
      // this write had no error check, so a failed flip-to-applied silently
      // left the suggestion visible — the optimizer would re-suggest the
      // same action on the next run, confusing operators.
      // Core-100 R2 CORE-BC: recorded by the server. The old client UPDATE matched no RLS policy,
      // returned no error and changed nothing — the suggestion re-appeared on the next run.
      const { error: markAppliedError } = await supabase.rpc('decide_optimizer_action' as never, {
        p_action_id: action.id, p_outcome: 'accepted',
      } as never);
      if (markAppliedError) {
        console.error('[SuggestionsDrawer] failed to mark action applied:', markAppliedError);
        toast({
          title: 'Suggestion partially applied',
          description: 'The move succeeded but we could not mark the suggestion as applied; it may reappear.',
          variant: 'destructive',
        });
      }

      void notifyDayboardInvalidated({
        reason: 'optimizer-apply',
        shopId: activeShop?.id || '',
        ticketId: action.ticket_id,
      });

      if (hasOtherSuggestions) {
        setRecalculating(true);
        toast({
          title: 'Recalculating...',
          description: 'Updating remaining suggestions based on this change'
        });
        
        await triggerReOptimization();
        await loadSuggestions();
      } else {
        toast({ 
          title: 'Suggestion Applied', 
          description: 'Schedule optimized successfully' 
        });
        await loadSuggestions();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to apply suggestion', variant: 'destructive' });
    } finally {
      setLoading(false);
      setRecalculating(false);
    }
  };

  /**
   * 8G.2D.2B §8/§9 — one confirmed split attempt owns ONE operation id.
   *
   * The database refuses a second family for the same key, but that only helps
   * if a RETRY carries the same key. The Edge function used to mint one per
   * invocation, so a lost HTTP response followed by a retry looked like a new
   * human decision. The key is therefore decided on this side and reused by
   * every retry of the same attempt.
   *
   * 8G.2D.2D.2 §6 — it is DERIVED from the proposal, not minted and stored. It
   * was previously keyed by `action.id` in an in-memory Map, which survived a
   * rerender and nothing else: after a reload the same human decision produced
   * a new id and the authority could no longer recognise it as already
   * applied. Deriving it means no state has to survive anything.
   *
   * 8G.2D.2D.4 §6 — the derivation lives in `splitSurfaceIdentity` so it can
   * be tested and broken on purpose, and so the schedule this request sends is
   * the same value the identity hashed.
   */
  const executeSplit = async (action: OptimizerAction) => {
    if (!action.metadata?.splitGroups || !activeShop?.id) return;

    /**
     * 8G.2D.2D.4 §2/§6 — THE SCHEDULE IS PART OF THE DECISION, SO IT IS PART
     * OF THE IDENTITY.
     *
     * MEASURED ALIAS: the identity was built from source + job ids + tech ids
     * only, while the REQUEST also carries this value. `split-ticket` uses
     * `scheduledStartTime || originalTicket.start_time` as sequence 1's start
     * and puts the result in `p_parts[0].start_time`. So two requests that
     * differ ONLY here produced the SAME operation id while asking the
     * authority for materially different structural acts — and the authority
     * never compares `p_parts`: it answers ALREADY_APPLIED and returns the
     * FIRST family.
     *
     * That is an INPUT-CANONICALIZATION collision. SHA-256 cannot help: the
     * two proposals hashed the same bytes because the differing field was
     * never in them.
     *
     * Computed ONCE and used for both the identity and the request, so the
     * value that is hashed cannot drift from the value that is sent.
     */
    // Built in `splitSurfaceIdentity` so a test can call the real thing and a
    // controlled breakage can actually fail, rather than a pin matching a
    // variable name in this file.
    const scheduledStart = effectiveScheduledStartTime(action);
    const operationId = await deriveSuggestionsSplitOperationId(action);
    const hasOtherSuggestions = suggestions.length > 1;
    
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('split-ticket', {
        body: {
          originalTicketId: action.ticket_id,
          splitGroups: action.metadata.splitGroups.map(g => ({ jobIds: g.jobIds, suggestedTechId: g.suggestedTechId })),
          // The SAME value the identity was derived from — one helper feeds
          // both, so the hashed proposal and the sent proposal cannot diverge.
          scheduledStartTime: scheduledStart ?? undefined,
          shopId: activeShop.id,
          operationId,
        }
      });

      if (error) throw error;

      // FIX-R35-CRIT-3: same surface-the-failure pattern for the split path.
      const { error: markSplitAppliedError } = await supabase.rpc('decide_optimizer_action' as never, {
        p_action_id: action.id, p_outcome: 'accepted',
      } as never);
      if (markSplitAppliedError) {
        console.error('[SuggestionsDrawer] failed to mark split action applied:', markSplitAppliedError);
        toast({
          title: 'Split partially applied',
          description: 'The split succeeded but we could not mark the suggestion as applied; it may reappear.',
          variant: 'destructive',
        });
      }

      void notifyDayboardInvalidated({
        reason: 'optimizer-reassign',
        shopId: activeShop?.id || '',
        ticketId: action.ticket_id,
        metadata: { kind: 'split' },
      });

      if (hasOtherSuggestions) {
        setRecalculating(true);
        toast({
          title: 'Split Applied - Recalculating...',
          description: `Created ${data.newTickets?.length || 2} tickets. Updating remaining suggestions.`
        });
        
        await triggerReOptimization();
        await loadSuggestions();
      } else {
        toast({ 
          title: 'Ticket Split Successfully', 
          description: `Created ${data.newTickets?.length || 2} new tickets` 
        });
        await loadSuggestions();
      }
    } catch (error) {
      toast({ title: 'Error', description: await edgeRefusalMessage(error, 'Failed to split ticket'), variant: 'destructive' });
    } finally {
      setLoading(false);
      setRecalculating(false);
    }
  };

  const dismissSuggestion = async (actionId: string) => {
    // FIX-R35-CRIT-3: surface dismiss failures. A silent failure here meant
    // the suggestion card persisted in the UI after the user explicitly
    // dismissed it — confusing and hides any permission/RLS issue.
    // Core-100 R2 CORE-BC: a dismissal is recorded AS a dismissal (it used to write the same
    // `applied = true` as an acceptance, so the two could not be told apart).
    const { error: dismissErr } = await supabase.rpc('decide_optimizer_action' as never, {
      p_action_id: actionId, p_outcome: 'dismissed',
    } as never);
    if (dismissErr) {
      console.error('[SuggestionsDrawer] failed to dismiss suggestion:', dismissErr);
      toast({
        title: 'Dismiss failed',
        description: 'Could not dismiss this suggestion. Please try again.',
        variant: 'destructive',
      });
      return;
    }
    await loadSuggestions();
  };

  if (suggestions.length === 0) return null;

  return (
    <Card className="mb-6">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">Optimizer Suggestions</CardTitle>
          <Badge variant="secondary">{suggestions.length}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {suggestions.map((suggestion) => {
          const isSplit = suggestion.metadata?.type === 'split';
          const isCrossTech = suggestion.metadata?.type === 'cross-tech';
          const profGap = suggestion.metadata?.proficiencyGap;
          const ticket = suggestion.metadata?.ticket;
          const vehicleInfo = ticket ? `${ticket.vehicle_year || ''} ${ticket.vehicle_make || ''} ${ticket.vehicle_model || ''}`.trim() : '';
          const jobsList = ticket?.jobs?.map(j => j.name).join(', ') || '';

          return (
            <div key={suggestion.id} className={cn("p-4 border rounded-lg space-y-3", isSplit ? "bg-purple-500/5 border-purple-500/20" : isCrossTech ? "bg-blue-500/5 border-blue-500/20" : "bg-muted/50")}>
              {/* Customer & Vehicle Info */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm">{ticket?.customer_name || 'Unknown Customer'}</span>
                  <span className="text-muted-foreground">•</span>
                  <span className="font-mono text-xs text-muted-foreground">{suggestion.ticket_id}</span>
                  {ticket?.type && (
                    <Badge variant="outline" className="text-xs">
                      {ticket.type}
                    </Badge>
                  )}
                  {isSplit && <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-700">🔀 Split</Badge>}
                  {isCrossTech && <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-700">🔄 Cross-Tech</Badge>}
                  {profGap && (
                    <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-700">
                      {profGap.toFixed(0)}% gap
                    </Badge>
                  )}
                  {suggestion.delta_score > 0 && (
                    <Badge variant="outline" className="text-xs bg-green-500/10 text-green-700">
                      +{suggestion.delta_score} pts
                    </Badge>
                  )}
                </div>
                {vehicleInfo && (
                  <div className="text-sm text-muted-foreground">
                    🚗 {vehicleInfo}
                  </div>
                )}
                {jobsList && (
                  <div className="text-sm text-muted-foreground line-clamp-2">
                    🔧 {jobsList}
                  </div>
                )}
              </div>

              {/* Assignment Change */}
              {!isSplit && (
                <div className="flex items-center gap-3 text-sm bg-background/50 p-2 rounded">
                  <div className="flex-1">
                    <div className="text-xs text-muted-foreground mb-1">Current</div>
                    <div className="font-medium">{suggestion.metadata?.from_tech_name || 'Unassigned'}</div>
                    <div className="text-xs text-muted-foreground">
                      {suggestion.from_json.startTime ? format(new Date(suggestion.from_json.startTime), 'h:mm a') : 'No time'}
                    </div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-primary" />
                  <div className="flex-1">
                    <div className="text-xs text-muted-foreground mb-1">Optimized</div>
                    <div className="font-medium text-primary">{suggestion.metadata?.to_tech_name || 'Unknown'}</div>
                    <div className="text-xs text-muted-foreground">
                      {format(new Date(suggestion.to_json.startTime), 'h:mm a')}
                    </div>
                  </div>
                </div>
              )}

              {/* Reason & Details */}
              {isSplit && suggestion.metadata?.splitGroups ? (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground mb-1">
                    {suggestion.metadata.splitType === 'proficiency' 
                      ? `Split by proficiency (${suggestion.metadata.proficiencyGap?.toFixed(0)}% gap)`
                      : `Split by skill level (${suggestion.metadata.skillGap?.toFixed(1)} tier gap)`}
                  </p>
                  {suggestion.metadata.splitGroups.map((group, idx) => (
                    <div key={idx} className="bg-background/50 p-2 rounded border text-xs">
                      <div className="font-medium">Part {idx + 1}</div>
                      <div>{group.totalMinutes}min · L{group.avgSkillLevel.toFixed(1)} → {group.suggestedTechName}</div>
                      <div className="text-muted-foreground">{group.reasoning}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">{suggestion.reason}</p>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                {isSplit ? (
                  <Button onClick={() => executeSplit(suggestion)} disabled={loading} size="sm" className="w-full">Execute Split</Button>
                ) : (
                  <Button onClick={() => applySuggestion(suggestion)} disabled={loading} size="sm"><CheckCircle className="h-4 w-4" /></Button>
                )}
                <Button onClick={() => dismissSuggestion(suggestion.id)} disabled={loading} size="sm" variant="ghost"><XCircle className="h-4 w-4" /></Button>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
